import { viennaToday } from '@pulsify/shared'
import type { ListingMenuKind } from '@pulsify/shared/dto'
import { Link } from '@tanstack/react-router'
import { Plus, Store } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { pickPlace, placeOptions } from '~/entities/listing/lib/place-picker'
import { useListing, useListings } from '~/entities/listing/model/use-listings'
import {
  useDeleteListingMenu,
  useListingMenus,
  useSaveFullMenu,
  useSaveLunchMenu,
} from '~/entities/listing-menu/model/use-listing-menus'
import { listingsInScope } from '~/entities/profile/lib/profile-scope'
import { useProfileScope } from '~/entities/profile/model/use-profiles'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/shared/ui/select'
import { Skeleton } from '~/shared/ui/skeleton'
import { PageHeader } from '~/widgets/page-header'
import { useMenuScan } from '../hooks/use-menu-scan'
import {
  countScannedDishes,
  emptyDraft,
  fromSavedMenu,
  fromScannedMenu,
  type MenuDraft,
  toFullMenuInput,
  toLunchMenuInput,
} from '../lib/menu-editor-model'
import { lunchPublishedNotice, summarizeFullMenu, summarizeLunchMenu } from '../lib/menu-summary'
import type { ScanFailure } from '../lib/scan-failure'
import { MenuCard } from './menu-card'
import { type EditorNotice, MenuEditor } from './menu-editor'
import { MenuReadingStage } from './menu-reading-stage'
import { MenuScanPanel } from './menu-scan-panel'

type Stage =
  | { step: 'overview' }
  | { step: 'capture'; listingId: string; kind: ListingMenuKind; files: File[] }
  | { step: 'reading'; listingId: string; kind: ListingMenuKind; files: File[] }
  | { step: 'editing'; listingId: string; draft: MenuDraft; notice: EditorNotice }

const FAILURE_MESSAGES: Record<ScanFailure, () => string> = {
  no_credits: m.menuScanErrorNoCredits,
  unsupported_file: m.menuScanErrorUnsupported,
  file_too_large: m.menuScanErrorTooLarge,
  too_many_files: m.menuScanErrorTooMany,
  unavailable: m.menuScanErrorUnavailable,
  busy: m.menuScanErrorBusy,
  failed: m.menuScanErrorFailed,
}

export function MenuPage({ listingId: wantedId }: { listingId?: string }) {
  const locale = getLocale()
  const [today] = useState(() => viennaToday(new Date()))
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const places = useListings(businessId)
  const { scope } = useProfileScope()
  const [pickedId, setSelectedId] = useState(wantedId)
  const selectedId = scope.kind === 'place' ? scope.listingId : pickedId
  const onFirstPage = !selectedId || places.listings.some((place) => place.id === selectedId)
  const requested = useListing(businessId, onFirstPage ? '' : (selectedId ?? ''))
  const options = listingsInScope(placeOptions(places.listings, requested.data), scope)
  const listing = requested.isLoading ? null : pickPlace(options, selectedId)
  const listingId = listing?.id ?? ''
  const menus = useListingMenus(businessId, listingId)
  const removeMenu = useDeleteListingMenu(businessId)
  const saveLunch = useSaveLunchMenu(businessId)
  const saveFull = useSaveFullMenu(businessId)
  const scan = useMenuScan(businessId)
  const [stage, setStage] = useState<Stage>({ step: 'overview' })

  async function remove(kind: ListingMenuKind) {
    try {
      await removeMenu.mutateAsync({ listingId, kind })
      toast.success(kind === 'lunch' ? m.menuLunchRemoved() : m.menuFullRemoved())
    } catch (error) {
      toast.error(getErrorMessageFromError(error))
    }
  }

  function edit(kind: ListingMenuKind) {
    const saved = kind === 'lunch' ? menus.data?.lunch : menus.data?.full
    if (saved)
      setStage({
        step: 'editing',
        listingId,
        draft: fromSavedMenu(saved, today, locale),
        notice: null,
      })
  }

  function writeYourself(kind: ListingMenuKind) {
    setStage({ step: 'editing', listingId, draft: emptyDraft(kind, today), notice: null })
  }

  async function read(placeId: string, kind: ListingMenuKind, files: File[]) {
    const savedLunch = kind === 'lunch' ? (menus.data?.lunch ?? null) : null
    setStage({ step: 'reading', listingId: placeId, kind, files })
    const outcome = await scan.run(placeId, kind, files)
    if (outcome.status === 'done') {
      const nothing =
        outcome.warnings.includes('nothing_recognised') || countScannedDishes(outcome.menu) === 0
      setStage({
        step: 'editing',
        listingId: placeId,
        draft: fromScannedMenu(outcome.menu, today, locale, savedLunch),
        notice: nothing ? 'nothing_recognised' : 'read',
      })
      return
    }
    if (outcome.status === 'unusable') {
      const names = new Set(outcome.files.map((file) => file.name))
      setStage({
        step: 'capture',
        listingId: placeId,
        kind,
        files: files.filter((file) => !names.has(file.name)),
      })
      for (const file of outcome.files) {
        toast.error(
          file.reason === 'undecodable'
            ? m.menuFileUndecodable({ name: file.name })
            : m.menuFileTooLarge({ name: file.name }),
        )
      }
      return
    }
    setStage({ step: 'capture', listingId: placeId, kind, files })
    if (outcome.status === 'cancelled') toast(m.menuReadingCancelled())
    else toast.error(FAILURE_MESSAGES[outcome.failure](), { description: m.menuScanNoCharge() })
  }

  async function publish(placeId: string, draft: MenuDraft) {
    try {
      if (draft.kind === 'lunch') {
        const result = toLunchMenuInput(draft)
        if (!result.ok) return
        await saveLunch.mutateAsync({ listingId: placeId, input: result.input })
        if (lunchPublishedNotice(result.input, today) === 'live') {
          toast.success(m.menuLunchPublished())
        } else {
          toast.warning(m.menuLunchSavedNotVisible(), { description: m.menuLunchPast() })
        }
      } else {
        const result = toFullMenuInput(draft)
        if (!result.ok) return
        await saveFull.mutateAsync({ listingId: placeId, input: result.input })
        toast.success(m.menuFullPublished())
      }
      setStage({ step: 'overview' })
    } catch (error) {
      toast.error(getErrorMessageFromError(error))
    }
  }

  const picker = options.length > 1 && (
    <Select value={listingId} onValueChange={setSelectedId} disabled={stage.step !== 'overview'}>
      <SelectTrigger className="w-full sm:w-64" aria-label={m.menuPlacePicker()}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((place) => (
          <SelectItem key={place.id} value={place.id}>
            {place.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )

  function body() {
    if (!business || places.isLoading || requested.isLoading || (listing && menus.isLoading)) {
      return (
        <div className="grid gap-6 lg:grid-cols-2">
          <Skeleton className="h-64 rounded-xl" />
          <Skeleton className="h-64 rounded-xl" />
        </div>
      )
    }
    if (!listing) {
      return (
        <Card className="flex flex-col items-center gap-3 py-16 text-center">
          <Store className="size-8 text-muted-foreground" aria-hidden />
          <p className="max-w-sm text-muted-foreground text-sm">{m.menuNoPlaces()}</p>
          <Button asChild>
            <Link to="/setup/profile" search={{ kind: 'place' }}>
              <Plus />
              {m.newPlace()}
            </Link>
          </Button>
        </Card>
      )
    }
    if (!menus.data) {
      return (
        <Card className="flex flex-col items-center gap-3 py-16 text-center">
          <p className="text-muted-foreground text-sm">{m.menuLoadError()}</p>
          <Button type="button" variant="outline" onClick={() => menus.refetch()}>
            {m.tryAgain()}
          </Button>
        </Card>
      )
    }
    if (stage.step === 'editing') {
      return (
        <MenuEditor
          draft={stage.draft}
          locale={locale}
          notice={stage.notice}
          publishing={saveLunch.isPending || saveFull.isPending}
          onChange={(draft) => setStage({ ...stage, draft })}
          onPublish={() => publish(stage.listingId, stage.draft)}
          onDiscard={() => setStage({ step: 'overview' })}
        />
      )
    }
    if (stage.step === 'reading') {
      return (
        <MenuReadingStage
          kind={stage.kind}
          files={stage.files}
          dishes={scan.dishes}
          onCancel={scan.cancel}
        />
      )
    }
    if (stage.step === 'capture') {
      const saved = stage.kind === 'lunch' ? menus.data.lunch : menus.data.full
      return (
        <Card className="flex flex-col gap-5 p-6">
          <div className="flex flex-col gap-1">
            <h2 className="font-semibold font-serif text-2xl text-foreground tracking-tight">
              {stage.kind === 'lunch' ? m.menuReadLunchHeading() : m.menuReadFullHeading()}
            </h2>
            {saved && <p className="text-muted-foreground text-sm">{m.menuRescanHint()}</p>}
          </div>
          <MenuScanPanel
            businessId={businessId}
            initialFiles={stage.files}
            onScan={(files) => read(stage.listingId, stage.kind, files)}
          />
          <Button
            type="button"
            variant="ghost"
            className="w-fit"
            onClick={() => setStage({ step: 'overview' })}
          >
            {m.cancel()}
          </Button>
        </Card>
      )
    }
    const { lunch, full } = menus.data
    return (
      <div className="grid items-start gap-6 lg:grid-cols-2">
        <MenuCard
          kind="lunch"
          summary={lunch ? summarizeLunchMenu(lunch, today) : null}
          locale={locale}
          removing={removeMenu.isPending}
          onRemove={() => remove('lunch')}
          onEdit={() => edit('lunch')}
          onRescan={() => setStage({ step: 'capture', listingId, kind: 'lunch', files: [] })}
          onWriteYourself={() => writeYourself('lunch')}
          upload={
            <MenuScanPanel
              businessId={businessId}
              onScan={(files) => read(listingId, 'lunch', files)}
            />
          }
        />
        <MenuCard
          kind="full"
          summary={full ? summarizeFullMenu(full) : null}
          locale={locale}
          removing={removeMenu.isPending}
          onRemove={() => remove('full')}
          onEdit={() => edit('full')}
          onRescan={() => setStage({ step: 'capture', listingId, kind: 'full', files: [] })}
          onWriteYourself={() => writeYourself('full')}
          upload={
            <MenuScanPanel
              businessId={businessId}
              onScan={(files) => read(listingId, 'full', files)}
            />
          }
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader title={m.menuPageTitle()} description={m.menuPageDescription()} action={picker} />
      {body()}
    </div>
  )
}
